import { memo } from 'react'
import { MenuCategory } from '../../types'
import MenuItem from './MenuItem'

interface MenuSectionProps {
  category: MenuCategory
  onItemClick: (itemId: string) => void
}

/**
 * MenuSection Component
 *
 * Displays a menu category with its items organized in a grid layout.
 * Handles the display of category name, description, and menu items.
 *
 * Optimized with React.memo to prevent unnecessary re-renders
 */
const MenuSection: React.FC<MenuSectionProps> = memo(({ category, onItemClick }) => {
  return (
    <section className="mx-auto">
      {/* Category Header */}
      <div className="mb-4">
        <h2 className="text-base sm:text-2xl font-bold text-gray-900 mb-1">
          {category.name}
        </h2>
        {category.description && (
          <p className="text-gray-600 text-sm">
            {category.description}
          </p>
        )}
      </div>

      {/* Menu Items Grid */}
      {category.items.length > 0 ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {category.items.map((item) => (
            <MenuItem
              key={item.id}
              item={item}
              onClick={() => onItemClick(item.id)}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <div className="text-4xl mb-2">🍽️</div>
          <p className="text-gray-500">No items available in this category</p>
        </div>
      )}
    </section>
  )
})

MenuSection.displayName = 'MenuSection'

export default MenuSection
